import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { useAuth, hasRole, isAdmin } from "../context/AuthContext";
import { useLookups } from "../context/LookupOptionsContext";
import {
  buildSalesContractPayload,
  brokerLabel,
  buyerLabel,
  contractToForm,
  deliveryLabel,
  packingLabel,
  paymentLabel,
  productSummary,
  quantityLabel,
  rateLabel,
  sortPartiesByName,
  upsertParty,
  validateSalesContractForm,
  varietyLabel,
} from "../lib/salesContractOptions";
import { Pill } from "./Ui";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Lock, Trash2 } from "lucide-react";
import SalesContractFormFields from "./SalesContractFormFields";
import SalesContractDocumentActions from "./SalesContractDocumentActions";
import SalesContractFulfillmentPanel from "./SalesContractFulfillmentPanel";

export default function SalesContractDetailDialog({
  contractId,
  open,
  onOpenChange,
  onSaved,
  onDeleted,
  partyPickers = null,
}) {
  const { user } = useAuth();
  const canEdit = isAdmin(user) || hasRole(user, ["manager", "purchaser"]);
  const canSoftDelete = user?.role === "manager" || isAdmin(user);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contract, setContract] = useState(null);
  const [form, setForm] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const isClosed = contract?.status === "CLOSED";
  const readOnly = !canEdit;

  useEffect(() => {
    if (!open || !contractId) {
      setContract(null);
      setForm(null);
      return;
    }
    setLoading(true);
    api
      .get(`/sales-contracts/${contractId}`)
      .then((r) => {
        setContract(r.data);
        setForm(contractToForm(r.data));
      })
      .catch((err) => {
        toast.error(extractError(err));
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, contractId, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    setBuyers(sortPartiesByName(partyPickers?.buyers ?? []));
    setBrokers(sortPartiesByName(partyPickers?.brokers ?? []));
  }, [open, partyPickers]);

  const handleSave = async (e) => {
    e.preventDefault();
    const err = validateSalesContractForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSubmitting(true);
      await api.patch(`/sales-contracts/${contractId}`, buildSalesContractPayload(form));
      toast.success("Contract updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async () => {
    try {
      setSubmitting(true);
      await api.post(`/sales-contracts/${contractId}/close`);
      toast.success("Contract closed");
      setConfirmClose(false);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      await api.delete(`/sales-contracts/${contractId}`);
      toast.success("Contract removed");
      setConfirmDelete(false);
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const footerActions = (
    <DialogFooter className="sm:justify-between gap-2 w-full">
      <div className="flex flex-wrap gap-2">
        {!readOnly && canEdit && contract?.status === "ACTIVE" && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmClose(true)}
            disabled={submitting}
            data-testid="sales-contract-close-btn"
          >
            <Lock className="h-4 w-4 mr-1" /> Close contract
          </Button>
        )}
        {canSoftDelete && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setConfirmDelete(true)}
            disabled={submitting}
          >
            <Trash2 className="h-4 w-4 mr-1" /> Archive
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly && (
          <Button
            type="submit"
            form="sales-contract-edit-form"
            disabled={submitting}
            size="sm"
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
          >
            {submitting ? "Saving…" : "Save changes"}
          </Button>
        )}
      </div>
    </DialogFooter>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-4xl w-[min(56rem,calc(100vw-1.5rem))] max-h-[min(92vh,900px)] p-0 gap-0 overflow-hidden flex flex-col"
          data-testid="sales-contract-detail-dialog"
        >
          <div className="shrink-0 px-5 pt-5 pb-3 pr-12 border-b border-border/80">
            <DialogHeader className="space-y-1">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0">
                  <DialogTitle className="font-mono text-base sm:text-lg truncate">
                    {contract?.contract_number || "Sales contract"}
                  </DialogTitle>
                  <DialogDescription className="text-xs sm:text-sm">
                    {readOnly
                      ? "View contract details"
                      : isClosed
                        ? "Edit closed contract"
                        : "Edit contract details"}
                    {contract && (
                      <span className="block mt-0.5 text-[11px] text-muted-foreground/90">
                        {contract.created_by && `Created by ${contract.created_by}`}
                        {contract.updated_by && ` · Updated by ${contract.updated_by}`}
                      </span>
                    )}
                  </DialogDescription>
                </div>
                {contractId && contract && (
                  <div className="shrink-0">
                    <SalesContractDocumentActions
                      contractId={contractId}
                      contractNumber={contract.contract_number}
                    />
                  </div>
                )}
              </div>
            </DialogHeader>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4">
            {loading || !form ? (
              <p className="text-sm text-muted-foreground py-10 text-center">Loading…</p>
            ) : readOnly ? (
              <div className="space-y-5">
                <ReadOnlyBody contract={contract} />
                <SalesContractFulfillmentPanel
                  contractId={contract.id}
                  contractNumber={contract.contract_number}
                  compact
                />
              </div>
            ) : (
              <form id="sales-contract-edit-form" onSubmit={handleSave} className="space-y-5">
                <SalesContractFormFields
                  form={form}
                  setForm={setForm}
                  contractNumber={contract.contract_number}
                  buyers={buyers}
                  brokers={brokers}
                  onBuyersChange={(party) => setBuyers((prev) => upsertParty(prev, party))}
                  onBrokersChange={(party) => setBrokers((prev) => upsertParty(prev, party))}
                  compact
                />
                <SalesContractFulfillmentPanel
                  contractId={contract.id}
                  contractNumber={contract.contract_number}
                  compact
                />
              </form>
            )}
          </div>

          {!loading && form && (
            <div className="shrink-0 border-t border-border/80 px-5 py-3 bg-muted/30">
              {footerActions}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close contract?</AlertDialogTitle>
            <AlertDialogDescription>
              Contract <strong>{contract?.contract_number}</strong> will be marked closed and can no longer be edited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClose} className="bg-bansal-sage hover:bg-bansal-sageDark">
              Close contract
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive contract?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive contract <strong>{contract?.contract_number}</strong>. It will no longer appear in lists.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ReadOnlyBody({ contract: c }) {
  const { optionsFor } = useLookups();
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5 text-sm">
      <Dt label="Date">{c.contract_date}</Dt>
      <Dt label="Buyer" className="col-span-2 sm:col-span-2">{buyerLabel(c)}</Dt>
      <Dt label="Crop">{c.crop_name || "—"}</Dt>
      <Dt label="Rice type">{c.rice_type_name || "—"}</Dt>
      <Dt label="Product">{c.product_name || "—"}</Dt>
      <Dt label="Item group">{c.item_group_name || "—"}</Dt>
      <Dt label="Item">{c.item_name || varietyLabel(c)}</Dt>
      <Dt label="Quantity">{quantityLabel(c)}</Dt>
      <Dt label="Rate">{rateLabel(c)}</Dt>
      <Dt label="Packing">{packingLabel(c, optionsFor("rice_packing"))}</Dt>
      {c.product_family === "RICE_BRAN" && (c.packing === "PP_PACKING" || c.packing === "PP_50KG") ? (
        <Dt label="Bag deduction">150 gm / bag (PPE)</Dt>
      ) : null}
      {c.product_family !== "RICE_BRAN" && c.bardana_gm ? (
        <Dt label="Bardana">{c.bardana_gm} gm {c.bardana_basis === "PER_QTL" ? "per qtl" : "per bag"}</Dt>
      ) : null}
      <Dt label="Payment">{paymentLabel(c)}</Dt>
      <Dt label="Cash discount">{c.cd_applicable ? `${c.cd_percent}%` : "No"}</Dt>
      <Dt label="Broker">{brokerLabel(c)}</Dt>
      <Dt label="Delivery">{deliveryLabel(c)}</Dt>
      <Dt label="Commercial">{productSummary(c)}</Dt>
      <Dt label="Status">
        <Pill tone={c.status === "CLOSED" ? "muted" : "sage"}>{c.status}</Pill>
      </Dt>
      {c.notes ? <Dt label="Notes" className="col-span-2 sm:col-span-3">{c.notes}</Dt> : null}
    </dl>
  );
}

function Dt({ label, children, className }) {
  return (
    <div className={className}>
      <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}
